import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { authAPI } from '../utils/api';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  if (!token) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="w-full max-w-sm text-center space-y-4">
          <p className="text-slate-600 text-sm">Invalid or missing reset link.</p>
          <Link to="/login" className="text-green-700 text-sm font-medium hover:underline">
            Back to sign in
          </Link>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    setLoading(true);
    try {
      await authAPI.resetPassword(token, password);
      setDone(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <div className="w-full max-w-sm">

        <div className="text-center mb-8">
          <img
            src="https://customer-assets.emergentagent.com/job_f1fe9a42-12b3-4cb4-b6d2-85b551503a7f/artifacts/hqgml9p8_ncc%20logo.jpg"
            alt="Church Logo"
            className="w-16 h-16 rounded-2xl object-cover mx-auto mb-5 shadow-sm"
          />
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">
            Set New Password
          </h1>
          <p className="text-sm text-slate-500 mt-1">Choose a strong password for your account</p>
        </div>

        {done ? (
          <div className="bg-green-50 border border-green-200 rounded-xl px-5 py-4 text-center space-y-3">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-sm text-green-800 font-medium">Password updated!</p>
            <p className="text-sm text-green-700">Redirecting you to sign in…</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-1.5">
                New Password
              </label>
              <input
                id="password"
                type="password"
                required
                autoComplete="new-password"
                data-testid="reset-password-input"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setError(''); }}
                className="w-full h-11 px-4 rounded-lg border border-slate-200 bg-white text-sm focus:ring-2 focus:ring-green-600/20 focus:border-green-600 outline-none transition-all"
                placeholder="At least 8 characters"
              />
            </div>

            <div>
              <label htmlFor="confirm" className="block text-sm font-medium text-slate-700 mb-1.5">
                Confirm Password
              </label>
              <input
                id="confirm"
                type="password"
                required
                autoComplete="new-password"
                data-testid="reset-confirm-input"
                value={confirm}
                onChange={(e) => { setConfirm(e.target.value); setError(''); }}
                className="w-full h-11 px-4 rounded-lg border border-slate-200 bg-white text-sm focus:ring-2 focus:ring-green-600/20 focus:border-green-600 outline-none transition-all"
                placeholder="Re-enter your password"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="reset-submit-btn"
              className="w-full h-11 rounded-full bg-green-700 text-white font-semibold text-sm hover:bg-green-800 active:scale-[0.98] transition-all duration-200 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed mt-2"
            >
              {loading ? 'Updating…' : 'Update Password'}
            </button>

            {error && (
              <p data-testid="reset-error-msg" className="text-sm text-rose-600 text-center">
                {error}
              </p>
            )}

            <p className="text-center text-sm text-slate-500 pt-1">
              <Link to="/login" className="text-green-700 font-medium hover:underline">
                Back to sign in
              </Link>
            </p>
          </form>
        )}

      </div>
    </div>
  );
};

export default ResetPassword;
